'use client'

import { cn } from '@/lib/utils'
import { useStoreNavigation } from '@/store'
import { Typo3_834 } from '@/types'
import { Icon, LangSwitch } from '@/ui/components'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { Accordion } from 'radix-ui'
import { ComponentProps, memo, useEffect, useState } from 'react'
import { checkIsCurrentChild, getActiveItem } from './utils'

interface NavMobileProps
  extends Omit<
    ComponentProps<typeof Accordion.Root>,
    'type' | 'collapsible' | 'value' | 'defaultValue' | 'onValueChange'
  > {
  data: Typo3_834['navigation']
}

const navMobileTriggerStyle = cn(
  'global-focus group textstyle-lg-button inline-grid grid-cols-[auto_1fr] gap-1 text-lg! text-neutral-100 transition-[color,box-shadow] outline-none'
)

const NavMobileTriggerContent = ({
  children,
  hasChildren = true
}: {
  children: React.ReactNode
  hasChildren?: boolean
}) => (
  <>
    <span className="bg-accent-300 relative flex h-12 w-12 items-center justify-center">
      <span className="opacity-100 transition-[transform_opacity] duration-200 ease-out group-data-[state=open]:rotate-90 group-data-[state=open]:opacity-0">
        {hasChildren ? (
          <Icon icon="material-symbols:add" width="1.25rem" />
        ) : (
          <Icon icon="material-symbols:chevron-right" width="1.25rem" />
        )}
      </span>
      <span className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-200 group-data-[state=open]:opacity-100">
        <Icon
          icon="material-symbols:remove"
          width="1.25rem"
          className="group-data-[state=closed]:hidden"
        />
      </span>
    </span>
    <span className="bg-accent-300 px-md inline-flex h-full items-center text-left">
      {children}
    </span>
  </>
)

function NavMobile({ data = [], ...props }: NavMobileProps) {
  const { isNavOpen, setIsNavOpen } = useStoreNavigation()
  const pathname = usePathname()
  const [value, setValue] = useState('')
  const [dafaultValue, setDefaultValue] = useState('')

  const items = data
    ?.filter((item) => item.isHidden === 0)
    ?.map((item) => ({
      ...item,
      children: item?.children?.filter((child) => child.isHidden === 0)
    }))

  useEffect(() => {
    const activeItem = getActiveItem(items, pathname)
    setTimeout(() => {
      setDefaultValue(`item-${activeItem?.uid?.toString()}`)
    }, 0)
  }, [items])

  const changeValue = (value: string) => {
    if (value) {
      setValue(value)
    } else {
      if (isNavOpen) {
        setValue(dafaultValue)
      }
    }
  }

  useEffect(() => {
    setTimeout(() => {
      changeValue(dafaultValue)
    }, 0)
  }, [dafaultValue])

  return (
    <>
      <Accordion.Root
        {...props}
        type="single"
        value={value}
        onValueChange={(value) => changeValue(value)}
        collapsible
      >
        <ul className={cn('gap-xs flex flex-col')}>
          {items?.map((item, index) => (
            <li key={'nav-mobile-item-' + index?.toString()}>
              {item.children && item.children?.length > 0 ? (
                <Accordion.Item value={`item-${item.uid}`}>
                  <Accordion.Header>
                    <Accordion.Trigger className={navMobileTriggerStyle}>
                      <NavMobileTriggerContent>
                        {item.title}
                      </NavMobileTriggerContent>
                    </Accordion.Trigger>
                  </Accordion.Header>
                  <Accordion.Content className="data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down overflow-hidden">
                    <ul
                      className={cn(
                        'mt-xs',
                        'bg-accent-300 p-padding-md pt-xl flex flex-col text-neutral-100'
                      )}
                    >
                      <li className="text-accent-100 md:col-span-2">
                        <Link
                          href={item.link as string}
                          className={cn(item.link === pathname && 'underline')}
                          onClick={() => setIsNavOpen(false)}
                        >
                          {item.title}
                        </Link>
                      </li>
                      <li>
                        <ul className={cn('gap-gutter md:columns-2')}>
                          {item.children?.map((child, childIndex) => (
                            <li
                              key={childIndex?.toString()}
                              className="pt-gutter"
                            >
                              <Link
                                href={child.link as string}
                                className={cn(
                                  "textstyle-md-button data-[active=true]:focus:bg-accent data-[active=true]:text-accent-foreground focus-visible:ring-ring/50 [&_svg:not([class*='text-'])]:text-muted-foreground",
                                  checkIsCurrentChild(child.link, pathname) &&
                                    'underline'
                                )}
                                onClick={() => setIsNavOpen(false)}
                              >
                                {child.title}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </li>
                    </ul>
                  </Accordion.Content>
                </Accordion.Item>
              ) : (
                <Link
                  href={item.link as string}
                  className={navMobileTriggerStyle}
                  onClick={() => setIsNavOpen(false)}
                >
                  <NavMobileTriggerContent hasChildren={false}>
                    {item.title}
                  </NavMobileTriggerContent>
                </Link>
              )}
            </li>
          ))}
        </ul>
      </Accordion.Root>

      <LangSwitch
        data-lang-switch-mobile
        className="mt-lg textstyle-md-button"
      />
    </>
  )
}

export default memo(NavMobile)
